import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import type { SelectOption } from './Select';

export interface MultiSelectProps {
  label?: string;
  hint?: string;
  error?: string;
  options: SelectOption[];
  /** Selected option values. */
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  /** Test id for the trigger button. */
  'data-testid'?: string;
}

/**
 * MultiSelect — a themed multi-select dropdown, styled to match Select.
 *
 * Renders a trigger button summarising the current selection and an opaque,
 * dark-mode-aware popover of checkable options. Closes on outside click /
 * Escape. Selection is a plain string[] of option values.
 */
export function MultiSelect({
  label,
  hint,
  error,
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled,
  required,
  className,
  'data-testid': testId,
}: MultiSelectProps) {
  const fieldId = useId();
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const errorId = error ? `${fieldId}-error` : undefined;
  const hintId = hint && !error ? `${fieldId}-hint` : undefined;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (val: string) => {
    onChange(value.includes(val) ? value.filter((v) => v !== val) : [...value, val]);
  };

  const selectedLabels = options.filter((o) => value.includes(o.value)).map((o) => o.label);
  const summary =
    selectedLabels.length === 0
      ? ''
      : selectedLabels.length <= 2
        ? selectedLabels.join(', ')
        : `${selectedLabels.length} selected`;

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={fieldId}
          className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {required && (
            <span className="ml-0.5 text-danger-500" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      <div ref={containerRef} className="relative">
        <button
          type="button"
          id={fieldId}
          data-testid={testId}
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={errorId || hintId}
          className={cn(
            'flex w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2 text-left text-sm text-gray-900',
            'transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0',
            'disabled:cursor-not-allowed disabled:bg-surface-secondary disabled:opacity-60',
            'dark:bg-surface-dark-secondary dark:text-gray-100',
            error
              ? 'border-danger-500 focus:ring-danger-500'
              : 'border-border focus:border-brand-500 focus:ring-brand-500 dark:border-border-dark',
            className,
          )}
        >
          <span className={cn('truncate', !summary && 'text-gray-400 dark:text-gray-500')}>
            {summary || placeholder}
          </span>
          <svg
            className={cn(
              'h-4 w-4 shrink-0 text-gray-400 transition-transform',
              open && 'rotate-180',
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && !disabled && (
          <ul
            id={listboxId}
            role="listbox"
            aria-multiselectable="true"
            className={cn(
              'absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-lg border py-1 shadow-lg',
              'border-border bg-white dark:border-border-dark dark:bg-surface-dark-secondary',
            )}
          >
            {options.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                No options available
              </li>
            )}
            {options.map((option) => {
              const selected = value.includes(option.value);
              return (
                <li key={option.value} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    disabled={option.disabled}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (!option.disabled) toggle(option.value);
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                      'text-gray-900 dark:text-gray-100',
                      'hover:bg-brand-50 dark:hover:bg-brand-900/20',
                      selected && 'bg-brand-50 font-medium dark:bg-brand-900/30',
                      option.disabled && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        selected
                          ? 'border-brand-600 bg-brand-600 text-white dark:border-brand-500 dark:bg-brand-500'
                          : 'border-gray-300 dark:border-gray-600',
                      )}
                      aria-hidden="true"
                    >
                      {selected && (
                        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </span>
                    <span className="truncate">{option.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {error && (
        <p id={errorId} className="mt-1.5 text-sm text-danger-500" role="alert">
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
          {hint}
        </p>
      )}
    </div>
  );
}

export default MultiSelect;
